import { Dropdown, DropdownSkeleton } from '@carbon/react';
import { localized, useTranslation } from '@pos/i18n';
import { useQuery } from '@tanstack/react-query';
import { hoApi } from '../api';

interface FormOption {
  id: string;
  label: string;
}

/** The brand's forms (pages of kind form, CMS › Forms), up to 100, for a page's form block. */
async function listForms(brandId: string): Promise<FormOption[]> {
  const { data, error } = await hoApi.GET('/api/v1/cms/pages', {
    // Filters by column (CrudFilter) aren't in the generated query type.
    params: { query: { pageSize: 100, ...({ kind: 'form', brandId } as Record<string, string>) } },
  });
  if (!data) throw error;
  return data.items.map((p) => ({ id: String(p.id), label: `${localized(p.titleI18n as Partial<Record<string, string>>, p.slug)} (${p.slug})` }));
}

/** Picks which form a form block shows: the brand's forms by their key and title. */
export function FormPicker({
  id,
  label,
  brandId,
  value,
  onChange,
}: {
  id: string;
  label: string;
  brandId: string;
  value: string;
  onChange(next: string): void;
}) {
  const { t } = useTranslation();
  const forms = useQuery({ queryKey: ['ho', 'cms', 'forms', brandId], queryFn: () => listForms(brandId), enabled: Boolean(brandId) });
  if (forms.isLoading) return <DropdownSkeleton />;
  const items = forms.data ?? [];
  return (
    <Dropdown<FormOption>
      id={id}
      titleText={label}
      label={t('cms.forms.choose')}
      items={items}
      itemToString={(item) => item?.label ?? ''}
      selectedItem={items.find((f) => f.id === value)}
      helperText={brandId ? (items.length === 0 ? t('cms.forms.none') : undefined) : t('cms.forms.brandFirst')}
      disabled={!brandId}
      onChange={({ selectedItem }) => onChange(selectedItem?.id ?? '')}
    />
  );
}
